import { useRef } from 'react';

import ReactToPrint from 'react-to-print';
import { FcPrint } from 'react-icons/fc';
import styled from 'styled-components';

import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { MicrostoreColumnTemplateFormValues, MicrostoreLocationTemplateFormValues } from './FormHelper/DataMapper';
import { MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS } from '../constants/types';
import { MicrostoreColumnTemplateType } from 'data/__generated__';
import { getLocationDisplayName, getLocationStatus } from './utils/locationBadgeHelpers';

const SHELF_PRINT_COLORS = ['#fff7ef', '#f3ffef', '#eff6ff'];

const isLocationDoubleCoil = (location: MicrostoreLocationTemplateFormValues): boolean => {
    return (
        location.product?.isDoubleCoil ||
        location.productArrangement?.productsWithStockMax?.some((p) => p.product?.isDoubleCoil) ||
        false
    );
};

const getLocationBackgroundColorForPrint = (location: MicrostoreLocationTemplateFormValues): string => {
    const status = getLocationStatus(location);
    switch (status) {
        case 'empty':
            return '#FAFAFA';
        case 'configured':
            return '#FFFFFF';
        case 'incomplete':
            return '#FFF3E0';
        case 'error':
            return '#FFEBEE';
        default:
            return '#FFFFFF';
    }
};

export const PrintColumnTemplate = ({
    microstoreColumnTemplateFormValues,
}: {
    microstoreColumnTemplateFormValues: MicrostoreColumnTemplateFormValues;
}) => {
    const componentRef = useRef(null);

    const { name, type, shelfTemplates } = microstoreColumnTemplateFormValues;
    const typeLabel = MICROSTORE_COLUMN_TEMPLATE_TYPE_LABELS[type as MicrostoreColumnTemplateType] || type;

    return (
        <Container>
            <ReactToPrint
                trigger={() => (
                    <span>
                        <TotemPrimaryButton type="button" $isSecondaryStyle>
                            <PrintButtonContent>
                                <FcPrint size={20} />
                                <PrintLabel>Imprimer le template</PrintLabel>
                            </PrintButtonContent>
                        </TotemPrimaryButton>
                    </span>
                )}
                content={() => componentRef.current}
            />
            <HiddenComponent>
                <Printable ref={componentRef}>
                    <TemplateHeader>
                        <TemplateName>{name}</TemplateName>
                        <TemplateType>{typeLabel}</TemplateType>
                    </TemplateHeader>

                    {shelfTemplates.map((shelf, shelfIndex) => (
                        <ShelfSection key={shelfIndex}>
                            <ShelfHeader $backgroundColor={SHELF_PRINT_COLORS[shelfIndex % SHELF_PRINT_COLORS.length]}>
                                Étagère {shelfIndex + 1}
                                {shelf.locationTemplateTable.length > 0 &&
                                    ` (${shelf.locationTemplateTable[0]?.length || 0} × ${shelf.locationTemplateTable.length})`}
                            </ShelfHeader>

                            <ShelfGrid>
                                {shelf.locationTemplateTable.map((locationRow, rowIndex) => (
                                    <LocationRow key={rowIndex}>
                                        {locationRow.map((location, columnIndex) => {
                                            const status = getLocationStatus(location);
                                            const displayName = getLocationDisplayName(location);
                                            const backgroundColor = getLocationBackgroundColorForPrint(location);
                                            const isDoubleCoil = isLocationDoubleCoil(location);

                                            const hasStockInfo = location.stockMax !== null;
                                            const stockMaxDisplay =
                                                hasStockInfo && status !== 'empty'
                                                    ? `${location.stockMax ?? '-'}`
                                                    : null;

                                            return (
                                                <LocationCell
                                                    key={columnIndex}
                                                    $backgroundColor={backgroundColor}
                                                    $isEmpty={status === 'empty'}
                                                    $isDoubleCoil={isDoubleCoil}
                                                >
                                                    {status === 'empty' ? (
                                                        <EmptyCell>Vide</EmptyCell>
                                                    ) : (
                                                        <>
                                                            <LocationName>
                                                                {isDoubleCoil && '[DOUBLE SPIRE] - '}
                                                                {displayName}
                                                            </LocationName>
                                                            {stockMaxDisplay && (
                                                                <StockInfo>Stock: {stockMaxDisplay}</StockInfo>
                                                            )}
                                                        </>
                                                    )}
                                                </LocationCell>
                                            );
                                        })}
                                    </LocationRow>
                                ))}
                            </ShelfGrid>
                        </ShelfSection>
                    ))}
                </Printable>
            </HiddenComponent>
        </Container>
    );
};

const Container = styled.div`
    color: black;
`;

const PrintButtonContent = styled.div`
    display: flex;
    align-items: center;
`;

const PrintLabel = styled.span`
    margin-left: 5px;
`;

const HiddenComponent = styled.div`
    display: none;
`;

const Printable = styled.div`
    padding: 18px;
    color: black;

    @page {
        size: portrait;
        margin: 10mm 12mm;
    }
`;

const TemplateHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 2px solid black;
`;

const TemplateName = styled.h1`
    font-size: 22px;
    font-weight: 800;
    margin: 0;
    color: black;
`;

const TemplateType = styled.span`
    font-size: 18px;
    font-weight: 600;
    color: #444;
`;

const ShelfSection = styled.div`
    break-inside: avoid;
    border-top: 1px solid black;
    border-left: 1px solid black;

    & + & {
        border-top: none;
    }
`;

const ShelfHeader = styled.div<{ $backgroundColor: string }>`
    font-size: 15px;
    font-weight: 700;
    padding: 6px 8px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    border-bottom: 1px solid black;
    border-right: 1px solid black;
    color: black;
    text-align: center;
`;

const ShelfGrid = styled.div`
    display: flex;
    flex-direction: column;
`;

const LocationRow = styled.div`
    display: flex;
    flex-direction: row;
`;

const LocationCell = styled.div<{ $backgroundColor: string; $isEmpty: boolean; $isDoubleCoil: boolean }>`
    flex: ${({ $isDoubleCoil }) => ($isDoubleCoil ? '2 1 18px' : '1 1 0')};
    min-height: 50px;
    padding: 2px 4px;
    background-color: ${({ $backgroundColor }) => $backgroundColor};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    border-right: 1px solid black;
    border-bottom: 1px solid black;
`;

const EmptyCell = styled.span`
    font-size: 11px;
    color: #aaa;
    font-style: italic;
`;

const LocationName = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: black;
    word-break: break-word;
    line-height: 1.2;
`;

const StockInfo = styled.div`
    font-size: 10px;
    color: #666;
    margin-top: 2px;
`;
